import {
  Box,
  Card,
  Col,
  ContentItem,
  ContentItems,
  ContentWrapper,
  Descriptions,
  Flex,
  Row,
  Space,
  Text,
  TitledText,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import {
  useAccountTitleOptions,
  useAccountingTypeOptions,
  useAccountingTypes,
} from '@/features/master-table/hooks';
import {
  ActionOfReflectionStatusLabelModifier,
  DiscriminatedInspectionEvaluationModel,
  InspectionByExternalExpertReasonModifier,
  OpinionByAdministrativeReviewTeamLabelModifier,
  ProcessTypeLabelModifier,
  ProjectModel,
} from '@/features/project/projects/_models/project';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import {
  AmountUnitLabelModifier,
  FALLBACK_TEXT,
  formatValueByUnit,
} from '@/utils';
import { ReviewTypeLabelModifier } from '@/utils/enum';
import { type FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

const PropsModel = ProjectModel.pick({
  fiscalYear: true,
  sheetType: true,
  inspectionEvaluation: true,
});

type Props = z.infer<typeof PropsModel>;

export const InspectionEvaluation: FC<Props> = (props) => {
  const { t } = useTranslation();
  const { id } = useId();
  const { unit } = useLocaledAmount();
  const accountingTypes = useAccountingTypes();
  const generalAccountingTypeId = accountingTypes.data?.find(
    (accountingType) => accountingType.type === 'general',
  )?.id;
  const accountingTypeOptions = useAccountingTypeOptions();
  const accountTitleOptions = useAccountTitleOptions();
  const parsed = DiscriminatedInspectionEvaluationModel.safeParse(props);

  if (!parsed.success || parsed.data.sheetType !== 'RS') {
    return null;
  }

  const {
    inspectionAndImprovementByAdministrativeJurisdiction,
    inspectionByExternalExpert,
    processAndOpinionByAdministrativeReviewTeam,
    reflectionStatusBasedOnOpinion,
    summariesAndResponses,
    otherIndicatedItemsAndResponses,
  } = parsed.data.inspectionEvaluation;

  const hasReflectedAmountOfGeneralAccount =
    typeof reflectionStatusBasedOnOpinion?.reflectedAmountOfGeneralAccount ===
    'number';

  return (
    <>
      <ContentWrapper
        title={t('inspection-and-improvement-by-administrative-jurisdiction')}
      >
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            style={{ marginBottom: 24 }}
            items={[
              <PinpointMarker
                key="inspection-result"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.inspection-result',
                }}
              >
                <TitledText
                  title={t('inspection-result')}
                  text={
                    inspectionAndImprovementByAdministrativeJurisdiction?.inspectionResult
                  }
                />
              </PinpointMarker>,
              <PinpointMarker
                key="evaluation"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.evaluation',
                }}
              >
                <TitledText
                  title={t('evaluation')}
                  text={
                    inspectionAndImprovementByAdministrativeJurisdiction?.evaluation
                  }
                />
              </PinpointMarker>,
            ]}
          />

          <ContentItems
            items={[
              <PinpointMarker
                key="direction-of-improvement"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.direction-of-improvement',
                }}
              >
                <TitledText
                  title={t('direction-of-improvement')}
                  text={
                    inspectionAndImprovementByAdministrativeJurisdiction?.directionOfImprovement
                  }
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('inspection-by-external-expert')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="inspection-target"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.inspection-target',
                }}
              >
                <TitledText
                  title={t('inspection-target')}
                  text={
                    inspectionByExternalExpert?.reviewType &&
                    ReviewTypeLabelModifier[
                      inspectionByExternalExpert.reviewType
                    ]
                  }
                />
              </PinpointMarker>,
              <PinpointMarker
                key="last-implemented-fiscal-year"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.last-implemented-fiscal-year',
                }}
              >
                <TitledText
                  title={t('last-implemented-fiscal-year')}
                  text={inspectionByExternalExpert?.lastImplementedFiscalYear}
                />
              </PinpointMarker>,
              <PinpointMarker
                key="reason-of-target"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.reason-of-target',
                }}
              >
                <TitledText
                  title={t('reason-of-target')}
                  text={
                    inspectionByExternalExpert?.reason &&
                    InspectionByExternalExpertReasonModifier[
                      inspectionByExternalExpert.reason
                    ]
                  }
                />
              </PinpointMarker>,
            ]}
          />
          <ContentItems
            style={{ marginTop: 24 }}
            items={[
              <PinpointMarker
                key="opinion"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.opinion',
                }}
              >
                <TitledText
                  title={t('opinion')}
                  text={inspectionByExternalExpert?.opinion}
                />
              </PinpointMarker>,
              <PinpointMarker
                key="result-of-publishing"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.result-of-publishing',
                }}
              >
                <TitledText
                  title={t('result-of-publishing')}
                  text={inspectionByExternalExpert?.resultOfPublishing}
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper
        title={t('process-and-opinion-by-administrative-review-team')}
      >
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="process-and-opinion-by-administrative-review-team-opinion"
                pinpoint={{
                  path: { project: id },
                  field:
                    'rs.process-and-opinion-by-administrative-review-team-opinion',
                }}
              >
                <TitledText
                  title={t('opinion')}
                  text={
                    processAndOpinionByAdministrativeReviewTeam?.opinion &&
                    OpinionByAdministrativeReviewTeamLabelModifier[
                      processAndOpinionByAdministrativeReviewTeam.opinion
                    ]
                  }
                />
              </PinpointMarker>,
              <PinpointMarker
                key="process-and-opinion-by-administrative-review-team-details"
                pinpoint={{
                  path: { project: id },
                  field:
                    'rs.process-and-opinion-by-administrative-review-team-details',
                }}
              >
                <TitledText
                  title={t('details')}
                  text={processAndOpinionByAdministrativeReviewTeam?.context}
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('reflection-status-based-on-opinion')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="improvement-and-reflection-status"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.improvement-and-reflection-status',
                }}
              >
                <TitledText
                  title={t('improvement-and-reflection-status')}
                  text={
                    reflectionStatusBasedOnOpinion?.action &&
                    ActionOfReflectionStatusLabelModifier[
                      reflectionStatusBasedOnOpinion.action
                    ]
                  }
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            mode="fullLine"
            items={[
              <PinpointMarker
                key="reflected-amount"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.reflected-amount',
                }}
              >
                <Space size="small" direction="vertical">
                  <Text color="secondary" text={t('reflected-amount')} />

                  {hasReflectedAmountOfGeneralAccount ||
                  !!reflectionStatusBasedOnOpinion?.specialAccount?.length ? (
                    <Descriptions bordered>
                      {[
                        ...(hasReflectedAmountOfGeneralAccount
                          ? [
                              {
                                accountingTypeId: generalAccountingTypeId,
                                accountTitleId: null,
                                reflectedAmount:
                                  reflectionStatusBasedOnOpinion.reflectedAmountOfGeneralAccount,
                              },
                            ]
                          : []),
                        ...(reflectionStatusBasedOnOpinion.specialAccount ??
                          []),
                      ].map((value) => {
                        return (
                          <Fragment
                            key={`${value.accountingTypeId ?? ''}-${
                              value.accountTitleId ?? ''
                            }`}
                          >
                            <Descriptions.Item label={t('accounting-name')}>
                              {accountingTypeOptions.data?.find(
                                (option) =>
                                  option.value === value.accountingTypeId,
                              )?.label ?? FALLBACK_TEXT}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('account-title')}>
                              {accountTitleOptions.data?.find(
                                (option) =>
                                  option.value === value.accountTitleId,
                              )?.label ?? FALLBACK_TEXT}
                            </Descriptions.Item>
                            <Descriptions.Item
                              label={t('reflected-amount-with-unit', {
                                unit: AmountUnitLabelModifier[unit],
                              })}
                            >
                              {formatValueByUnit(value.reflectedAmount, {
                                operation: 'round',
                                unit,
                              })}
                            </Descriptions.Item>
                          </Fragment>
                        );
                      })}
                    </Descriptions>
                  ) : (
                    FALLBACK_TEXT
                  )}
                </Space>
              </PinpointMarker>,
            ]}
          />
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="details"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.reflection-status-based-on-opinion-details',
                }}
              >
                <TitledText
                  title={t('details')}
                  text={reflectionStatusBasedOnOpinion?.context}
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('indicated-item-and-response')}>
        <Flex vertical gap={12}>
          <Box paddingTop={16}>
            <PinpointMarker
              pinpoint={{
                path: { project: id },
                field: 'rs.summary-of-publishing-title',
              }}
            >
              <Text variant="h4" text={t('summary-of-publishing')} />
            </PinpointMarker>
          </Box>

          <Row gutter={[24, 16]}>
            {summariesAndResponses?.map((summaryAndResponse, index) => {
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                <Col span={12} key={index}>
                  <PinpointMarker
                    pinpoint={{
                      path: {
                        project: id,
                        summaryAndResponseProcess:
                          summaryAndResponse.process ?? '',
                      },
                      field: 'rs.summary-of-publishing',
                    }}
                  >
                    <Card>
                      <Row gutter={[0, 24]}>
                        <Col span={12}>
                          <TitledText
                            title={t('summary-process')}
                            text={
                              summaryAndResponse.process &&
                              ProcessTypeLabelModifier[
                                summaryAndResponse.process
                              ]
                            }
                          />
                        </Col>
                        <Col span={12}>
                          <TitledText
                            title={t('summary-fiscal-year')}
                            text={summaryAndResponse.fiscalYear}
                          />
                        </Col>
                        <Col span={24}>
                          <TitledText
                            title={t('summary-content')}
                            text={summaryAndResponse.content}
                          />
                        </Col>
                        <Col span={24}>
                          <TitledText
                            title={t('response-status')}
                            text={summaryAndResponse.response}
                          />
                        </Col>
                      </Row>
                    </Card>
                  </PinpointMarker>
                </Col>
              );
            })}
          </Row>

          <Box paddingTop={16}>
            <PinpointMarker
              pinpoint={{
                path: { project: id },
                field: 'rs.others-indicated-item-title',
              }}
            >
              <Text variant="h4" text={t('others-indicated-item')} />
            </PinpointMarker>
          </Box>

          <Row gutter={[24, 16]}>
            {otherIndicatedItemsAndResponses?.map(
              (otherIndicatedItemAndResponse, index) => {
                return (
                  // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                  <Col span={12} key={index}>
                    <PinpointMarker
                      pinpoint={{
                        path: {
                          project: id,
                          otherIndicatedItemAndResponseReason:
                            otherIndicatedItemAndResponse?.reason ?? '',
                        },
                        field: 'rs.others-indicated-item',
                      }}
                    >
                      <Card>
                        <Flex vertical gap={16}>
                          <TitledText
                            title={t('indicated-reason')}
                            text={otherIndicatedItemAndResponse.reason}
                          />
                          <TitledText
                            title={t('indicated-fiscal-year')}
                            text={otherIndicatedItemAndResponse.fiscalYear}
                          />
                          <TitledText
                            title={t('indicated-content')}
                            text={otherIndicatedItemAndResponse.content}
                          />
                          <TitledText
                            title={t('response-status')}
                            text={otherIndicatedItemAndResponse.response}
                          />
                        </Flex>
                      </Card>
                    </PinpointMarker>
                  </Col>
                );
              },
            )}
          </Row>
        </Flex>
      </ContentWrapper>
    </>
  );
};
